import * as React from "react";
import { toast } from "@grandvisionhq/common";
import { useMutation } from "@grandvisionhq/graphql";
import { useCartStorage } from "@grandvisionhq/lib-utils-cart";
import { useDispatchTrackErrorEvent } from "@grandvisionhq/svc-tealium/events";
import { useMutationConfig } from "./use-mutation-config.js";
import { useUpdateCartCache } from "./use-update-cart-cache.js";
import { getFlattenedCartFailureErrorCodes } from "../../../../utils/utils/get-flattened-cart-error-codes.js";
import { errorsMapping } from "../../../../mappers/mappers/errors-mapping.js";
import { SET_CUSTOMER_DETAILS } from "../../../../operations/mutations/operations/mutations/set-customer-details.js";
import { trimInputData } from "../../../../utils/utils/trim-input-data.js";
const useSetCustomerDetails = ({ onError, onCompleted } = {}) => {
  const [cart] = useCartStorage();
  const { updateCacheWithCart } = useUpdateCartCache();
  const dispatchTrackErrorEvent = useDispatchTrackErrorEvent();
  const handleOnCompleted = React.useCallback(
    (responseData) => {
      let result;
      if ("setCustomerDetails" in responseData) {
        result = responseData.setCustomerDetails;
        if (result.failures?.length) {
          const errorCodes = getFlattenedCartFailureErrorCodes(result.failures);
          onError?.({ reason: "MUTATION" });
          errorCodes.forEach((error) => toast.failed(error));
        }
        if (result.success) {
          onCompleted?.("MUTATION");
        }
      }
    },
    [onCompleted, onError]
  );
  const handleOnError = React.useCallback(() => {
    onError?.({ reason: "MUTATION" });
    dispatchTrackErrorEvent(
      () => Promise.resolve({
        Error_Source: "Server",
        Error_Code: "Set customer details error",
        Error_Details: "Mutation failed",
        Error_Message: errorsMapping.SOMETHING_WENT_WRONG
      })
    );
    toast.failed(errorsMapping.SOMETHING_WENT_WRONG);
  }, [dispatchTrackErrorEvent, onError]);
  const updateCache = React.useCallback(
    (cache, { data }) => {
      if (data && data.setCustomerDetails.success) {
        const updatedCart = data.setCustomerDetails.cart;
        updatedCart && updateCacheWithCart({ cart: updatedCart, cache });
      }
    },
    [updateCacheWithCart]
  );
  const { mutationOptions } = useMutationConfig({
    onError: handleOnError,
    onCompleted: handleOnCompleted
  });
  const [mutationCallback, mutation] = useMutation(SET_CUSTOMER_DETAILS, {
    ...mutationOptions,
    // reason: typescript complains about a typing mismatch for the data prop
    update: updateCache,
    context: { forwardHeaders: { "cart-hash": cart.cartHash } }
  });
  const setCustomerDetails = React.useCallback(
    async (customerDetails) => {
      if (!cart.cartId) {
        throw new Error("Need to have valid cart to update!");
      }
      const sanitizedData = trimInputData(customerDetails);
      return await mutationCallback({
        variables: {
          customerDetailsInput: {
            ...sanitizedData,
            checkoutId: cart.cartId
          }
        }
      }).then(({ data }) => {
        return { success: data?.setCustomerDetails.success };
      });
    },
    [cart.cartId, mutationCallback]
  );
  return {
    setCustomerDetails,
    isUpdating: mutation.loading,
    updateError: mutation.error
  };
};
export {
  useSetCustomerDetails
};
//# sourceMappingURL=use-set-customer-details.js.map
